
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { SignupForm } from "@/components/auth/SignupForm";
import { LoginForm } from "@/components/auth/LoginForm";
import { useTranslation } from "react-i18next";
import { useAuthForm } from "@/hooks/auth/useAuthForm";
import { AuthMode } from "@/hooks/auth/types";
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useNavigate } from "react-router-dom";
import { useToast } from "@/components/ui/use-toast";

interface InviteAuthViewProps {
  authMode: "signup" | "login";
  groupName: string;
  groupId: string; 
  onBackToInvite: () => void;
  onSubmitAuth: (e: React.FormEvent) => void;
  onSwitchToLogin: () => void;
  onSwitchToSignUp: () => void;
}

export const InviteAuthView = ({
  authMode: initialAuthMode,
  groupName,
  groupId,
  onBackToInvite,
  onSubmitAuth,
  onSwitchToLogin,
  onSwitchToSignUp
}: InviteAuthViewProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [authMode, setAuthMode] = useState<AuthMode>(initialAuthMode);
  
  // Pass the current authMode to useAuthForm
  const authForm = useAuthForm(authMode);
  
  // Update authMode when initialAuthMode changes
  useEffect(() => {
    setAuthMode(initialAuthMode);
  }, [initialAuthMode]);
  
  const {
    isLoading,
    username, setUsername,
    email, setEmail,
    password, setPassword,
    name, setName,
    step, setStep,
    avatars,
    selectedAvatar, setSelectedAvatar,
    loginIdentifier, setLoginIdentifier,
    avatarTab, setAvatarTab,
    customAvatarPreview,
    usernameStatus,
    bankName, setBankName,
    branchName, setBranchName,
    accountType, setAccountType,
    accountNumber, setAccountNumber,
    accountHolderName, setAccountHolderName,
    paypayUsername, setPaypayUsername,
    paymentTab, setPaymentTab,
    fileInputRef,
    handleNextStep,
    handlePrevStep,
    handleFileChange,
    clearCustomAvatar,
    handleAuth,
    handleSkipPaymentSetup
  } = authForm;

  // Enhanced handleSubmitAuth to handle both login and signup flows
  const handleSubmitAuth = async (e: React.FormEvent) => {
    e.preventDefault();
    console.log("InviteAuthView - handleSubmitAuth called for groupId:", groupId, "authMode:", authMode);
    
    if (authMode === "login") {
      if (!loginIdentifier || !password) {
        toast({
          title: "Error",
          description: t('auth.enterCredentials', "Please enter your username/email and password"),
          variant: "destructive",
        });
        return;
      }
      
      try {
        console.log("Attempting login with identifier:", loginIdentifier);
        
        const isEmail = loginIdentifier.includes('@');
        const signInEmail = isEmail ? loginIdentifier : `${loginIdentifier}@splitbill.local`;
        console.log("Normalized login email:", signInEmail);
        
        const { data, error: signInError } = await supabase.auth.signInWithPassword({
          email: signInEmail,
          password
        });
        
        if (signInError) {
          console.error("Sign in error:", signInError);
          throw signInError;
        }
        
        console.log("Login successful:", data);
        
        // Check if user is already a member of this group
        const { data: existingMember, error: checkError } = await supabase
          .from('group_members')
          .select('id')
          .eq('group_id', groupId)
          .eq('profile_id', data.user.id)
          .maybeSingle();
        
        if (checkError && checkError.code !== 'PGRST116') {
          console.error('Error checking membership:', checkError);
          throw checkError;
        }
        
        if (existingMember) {
          toast({
            description: t('groupInvite.alreadyMember'),
            className: "bg-gradient-to-r from-primary to-green text-white"
          });
          navigate(`/group/${groupId}`);
          return;
        }
        
        // Add user to the group
        const { error } = await supabase
          .from('group_members')
          .insert([{
            group_id: groupId,
            profile_id: data.user.id,
            is_admin: false
          }]);
        
        if (error) {
          console.error('Error joining group:', error);
          throw error;
        }
        
        toast({
          description: t('groupInvite.successJoined'),
          className: "bg-gradient-to-r from-primary to-green text-white"
        });
        
        navigate(`/group/${groupId}`);
      } catch (error: any) {
        console.error("Authentication error:", error);
        toast({
          title: "Error",
          description: error.message || "Login failed. Please check your credentials and try again.",
          variant: "destructive"
        });
      }
      return;
    } 
    else if (authMode === "signup") {
      // For signup, store the pendingInvite for post-registration handling
      console.log("Storing pendingInvite for post-signup handling:", groupId);
      localStorage.setItem('pendingInvite', groupId);
      
      // For step 1 and 2, just proceed to the next step
      if (step < 3) {
        handleNextStep();
        return;
      }
      
      // For the final step, proceed with the actual signup
      try {
        console.log("Proceeding with final signup step");
        await handleAuth(e);
        // Note: The actual group joining will be handled in App.tsx via the pendingInvite
      } catch (error: any) {
        console.error("Signup error:", error);
        toast({
          title: "Error",
          description: error.message || "Failed to create account",
          variant: "destructive"
        });
      }
    }
  };

  return (
    <div className="flex flex-1 items-center justify-center p-4">
      <Card className="w-full max-w-md border-2 border-primary/20 shadow-xl dark:border-primary/30">
        <div className="absolute h-1 w-full top-0 left-0 bg-gradient-to-r from-primary to-green"></div>
        <CardHeader className="text-center">
          <CardTitle className="text-xl sm:text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-primary to-green">
            {authMode === "signup" ? t('auth.createAccount') : t('auth.login')}
          </CardTitle>
          <p className="text-muted-foreground text-sm">
            {t('groupInvite.invited')}
          </p>
          <p className="text-sm font-medium mt-1">
            {groupName}
          </p>
        </CardHeader>
        <CardContent>
          {authMode === "signup" ? (
            <SignupForm
              step={step}
              isLoading={isLoading}
              name={name}
              setName={setName}
              email={email}
              setEmail={setEmail}
              username={username}
              setUsername={setUsername}
              password={password}
              setPassword={setPassword}
              usernameStatus={usernameStatus || "typing"}
              avatars={avatars}
              selectedAvatar={selectedAvatar}
              setSelectedAvatar={setSelectedAvatar}
              avatarTab={avatarTab}
              setAvatarTab={setAvatarTab}
              customAvatarPreview={customAvatarPreview}
              clearCustomAvatar={clearCustomAvatar}
              fileInputRef={fileInputRef}
              handleFileChange={handleFileChange}
              paymentTab={paymentTab}
              setPaymentTab={setPaymentTab}
              bankName={bankName}
              setBankName={setBankName}
              branchName={branchName}
              setBranchName={setBranchName}
              accountType={accountType}
              setAccountType={setAccountType}
              accountNumber={accountNumber}
              setAccountNumber={setAccountNumber}
              accountHolderName={accountHolderName}
              setAccountHolderName={setAccountHolderName}
              paypayUsername={paypayUsername}
              setPaypayUsername={setPaypayUsername}
              onSubmit={handleSubmitAuth}
              handlePrevStep={handlePrevStep}
              handleSkipPaymentSetup={handleSkipPaymentSetup}
              switchToLogin={() => {
                setAuthMode("login");
                onSwitchToLogin();
              }}
            />
          ) : (
            <LoginForm
              loginIdentifier={loginIdentifier}
              setLoginIdentifier={setLoginIdentifier}
              password={password}
              setPassword={setPassword}
              isLoading={isLoading}
              onSubmit={handleSubmitAuth}
              switchToSignUp={() => {
                setAuthMode("signup");
                onSwitchToSignUp();
              }}
            />
          )}
        </CardContent>
        <CardFooter className="flex justify-center pb-6">
          <Button
            onClick={onBackToInvite}
            variant="ghost"
            size="sm"
          >
            {t('auth.back')}
          </Button>
        </CardFooter>
      </Card>
    </div>
  );
};
